// Root component with routing definition

import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useRouteError,
  useLocation,
} from 'react-router-dom';
import TrailProvider from './components/TrailProvider';
import NavigationObserver from './components/NavigationObserver';
import AnalyticsObserver from './components/AnalyticsObserver';
import LoginRedirect from './components/LoginRedirect';
import Layout from './components/Layout';
import PageLogin from './pages/PageLogin';
import PageAdmin from './pages/PageAdmin';
import PageTournament from './pages/PageTournament';
import PageTeam from './pages/PageTeam';
import PagePlayer from './pages/PagePlayer';
import PageCoach from './pages/PageCoach';
import PageReferee from './pages/PageReferee';
import PageRefereeAssigner from './pages/PageRefereeAssigner';
import PageScheduleImport from './pages/PageScheduleImport';
import PageNone from './pages/PageNone';
import PageInvite from './pages/PageInvite';
import PageAuth from './pages/PageAuth';
import PageEmailLinkSignIn from './pages/PageEmailLinkSignIn';
import PagePaymentSuccess from './pages/PagePaymentSuccess';
import PageVerifyEmail from './pages/PageVerifyEmail';
import PageSchedulePrint from './pages/PageSchedulePrint';
import FirebaseContextProvider, {
  useFirebase,
} from './components/FirebaseContextProvider';
import ModalProvider from './components/ModalProvider';
import PageParent from './pages/PageParent';
import PageFieldManager from './pages/PageFieldManager';
import PageChats from './pages/PageChats';
import PageLive from './pages/PageLive';
import Spinner from './components/Spinner';
import PwaToast from './components/PwaToast';
import Home from './components/Home';
import { ToastContainer } from 'react-toastify';
import { gracePeriodHours } from './utils/consts';
import { UsersRoles } from './utils/enums';
import './App.scss';

const AdminOnly = ({ children }) => {
  const { user } = useFirebase();

  if (user?.role !== UsersRoles.ADMIN) {
    return <Navigate to='/login' replace />;
  }

  return <>{children}</>;
};

// Auth + email verification gate (outer wrapper)
const EmailVerificationRoutes = () => {
  const { user, isUserResolved, isEmailVerified, userCreated } = useFirebase();
  const location = useLocation();

  // Wait only until we know who the user is — the full users collection keeps
  // loading in the background behind the rendered app
  if (!isUserResolved)
    return (
      <Layout screenSized>
        <Spinner show={true} />
      </Layout>
    );

  // If email is not verified and we are not already on the verify page
  if (
    user &&
    !isEmailVerified &&
    !location.pathname.startsWith('/verify-email')
  ) {
    const createdTs = userCreated ? +userCreated : null;

    const isGraceExpired =
      !!createdTs &&
      Date.now() - createdTs >= gracePeriodHours * 60 * 60 * 1000;

    // Grace expired → force verify route
    if (isGraceExpired) return <Navigate to='/verify-email' replace />;
  }

  return <Outlet />;
};

// Private routes subset (inner wrapper)
// For now it matches EmailVerificationRoutes, but soon you can tighten it.
const PrivateRoutes = () => {
  const { user } = useFirebase();

  // Not authenticated → send to the root
  if (!user) return <Navigate to='/' replace />;

  return <Outlet />;
};

const PageError = ({ noUser }) => {
  let { status, data } = useRouteError();
  return (
    <Layout noUser={noUser} className='page-error'>
      <h1>{`${status} ${data}`}</h1>
    </Layout>
  );
};

const AnimatedOutlet = () => {
  const location = useLocation();
  return (
    <div key={location.pathname} className='page-transition'>
      <Outlet />
    </div>
  );
};

const ObserverRoute = () => {
  return (
    <ModalProvider>
      <NavigationObserver />
      <AnalyticsObserver />
      <LoginRedirect />
      <AnimatedOutlet />
      <PwaToast />
    </ModalProvider>
  );
};

const routes = [
  {
    element: <ObserverRoute />,
    errorElement: <PageError noUser />,
    children: [
      { path: '/login', element: <PageLogin />, errorElement: <PageError /> },
      {
        path: '/invite/:token',
        element: <PageInvite />,
        errorElement: <PageError />,
      },
      {
        path: '/auth',
        element: <PageAuth />,
        errorElement: <PageError />,
      },
      {
        path: '/emaillinksignin',
        element: <PageEmailLinkSignIn />,
        errorElement: <PageError />,
      },
      {
        path: '/payment-success',
        element: <PagePaymentSuccess />,
        errorElement: <PageError />,
      },
      {
        path: '/schedule/:tournamentId/:dayNumber',
        element: <PageSchedulePrint />,
        errorElement: <PageError />,
      },

      // Email verification superset
      {
        element: <EmailVerificationRoutes />,
        errorElement: <PageError />,
        children: [
          {
            path: '/tournaments/:tournamentId',
            element: <PageTournament />,
          },
          // Private routes subset
          {
            element: <PrivateRoutes />,
            errorElement: <PageError />,
            children: [
              {
                path: '/admin',
                element: (
                  <AdminOnly>
                    <PageAdmin />
                  </AdminOnly>
                ),
              },
              {
                path: '/verify-email',
                element: <PageVerifyEmail />,
                errorElement: <PageError />,
              },
              {
                path: '/teams/:teamId',
                element: <PageTeam />,
              },
              {
                path: '/players/:playerId',
                element: <PagePlayer />,
              },
              {
                path: '/coaches/:coachId',
                element: <PageCoach />,
              },
              {
                path: '/parents/:parentId',
                element: <PageParent />,
              },
              {
                path: '/field-managers/:managerId',
                element: <PageFieldManager />,
              },
              {
                path: '/referees/:refereeId',
                element: <PageReferee />,
              },
              {
                path: '/referee-assigners/:assignerId',
                element: <PageRefereeAssigner />,
              },
              {
                path: '/tournaments/:tournamentId/import',
                element: <PageScheduleImport />,
              },
              {
                path: '/none/:noneId',
                element: <PageNone />,
              },
              {
                path: '/chats',
                element: <PageChats />,
              },
              {
                path: '/chats/:chatId',
                element: <PageChats />,
              },
              // A notification still sitting on somebody's device points here.
              // The messages it named are gone; the chats took their place.
              {
                path: '/messages/:messageId',
                element: <Navigate to='/chats' replace />,
              },
              {
                path: '/live/:tournamentId?',
                element: <PageLive />,
              },
            ],
          },
        ],
      },

      { path: '/', element: <Home /> },
    ],
  },
];

const RouterWrap = () => {
  const router = createBrowserRouter(routes);
  return <RouterProvider router={router} />;
};

function App() {
  return (
    <FirebaseContextProvider>
      <TrailProvider>
        <RouterWrap />
        <ToastContainer theme='colored' />
      </TrailProvider>
    </FirebaseContextProvider>
  );
}

export default App;
